import { getCookie } from '@dna/utils'
import { md5 } from '@vendor/md5'

class SegmentClient {
  constructor() {
    this.deviceCategory =
      window.innerWidth < 768
        ? 'mobile'
        : window.innerWidth >= 1024
          ? 'desktop'
          : 'tablet'
    this.email =
      JSON.parse(window.localStorage.getItem('ajs_user_traits'))?.email ||
      undefined
    this.ometriaProfileId = this.getOmetriaProfileId()
    this.ometriaIdentified = false
    this.signedIn = false
    this.CONSTANTS = {
      BRAND: 'Rothys',
      CURRENCY: 'USD',
      SENT_FROM: 'website',
      EVENTS: eventNames,
    }
  }

  /**
   * identify
   *
   * Makes Segment's identify call.
   * Littledata auto-identifies in certain cases, other cases are up to us.
   *
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/shopify-littledata/
   *
   * @param  string   The user's e-mail (to be hashed)
   * @param  object   The user's traits
   * @param  boolean  Option to merge event data with shared props
   */
  identify({ email, id = false, traits = {} }) {
    // Add or overwrite commonly seen traits
    const commonProps = this.accessCommonProperties()
      .then(async () => {
        if (this.deviceCategory) traits.device_category = this.deviceCategory
        if (this.country) traits.countryCode = this.country
        if (this.presentmentCurrency)
          traits.currencyCode = this.presentmentCurrency
        if (this.language) traits.languageCode = this.language
        if (this.locale) traits.locale = this.locale
        if (window.navigator.userAgent) {
          traits.userAgent = window.navigator.userAgent
        }
        traits.signedIn = this.signedIn
        const ometriaProfileId = await this.ometriaProfileId
        if (ometriaProfileId) {
          traits.ometria_profile_id = ometriaProfileId
          this.ometriaIdentified = true
        }
        traits.ometria_identified = this.ometriaIdentified

        if (email || this.email) {
          traits.email = email || this.email
          traits.emailDomain = traits.email.split('@')[1]
          const userId = md5(traits.email)
          analytics.identify(userId, traits, this.consentProperties)

          if (id) {
            // When there's an email and an id available, use alias to merge
            // identities, treating hashed email as the canonical id.
            // NOTE: This explicitly changes a previously set id to the hashed email.
            const previousId = id
            if (userId !== previousId) {
              analytics.alias(userId, previousId)
            }
          }
        } else if (id) {
          // Had an id but not an email. Use the provided id so that
          // user activity can continue to be associated with it.
          analytics.identify(id, traits, this.consentProperties)
        } else {
          // User is anonymous.
          analytics.identify(traits, this.consentProperties)
        }
      })
      .catch((error) => {
        console.warn('Identify could not set common properties.')
        console.log(error.message)
      })
  }

  /**
   * track
   *
   * Makes Segment's track call.
   *
   * @param  string   The event name
   * @param  object   The event properties
   * @param  boolean  Option to merge event data with shared props
   */
  track(name, data, merge = true) {
    const eventProperties = data || {}
    eventProperties.email = this.email
    eventProperties.sent_from = this.CONSTANTS.SENT_FROM
    eventProperties.page_url = window.location.href.includes('?')
      ? window.location.href.split('?')[0]
      : window.location.href
    eventProperties.page_path = window.location.pathname.includes('?')
      ? window.location.pathname.split('?')[0]
      : window.location.pathname
    eventProperties.referrer = document.referrer

    const attentiveParams =
      'utm_medium=sms&utm_source=attentive&utm_campaign=category-abandonment-message'
    eventProperties.attentive_full_url = window.location.search
      ? `${window.location.href}&${attentiveParams}`
      : `${window.location.href}?${attentiveParams}`

    // Add or overwrite commonly seen properties
    const commonProps = this.accessCommonProperties()
      .then(async () => {
        if (this.deviceCategory)
          eventProperties.device_category = this.deviceCategory
        if (this.country) eventProperties.country = this.country
        if (this.CONSTANTS.CURRENCY)
          eventProperties.currency = this.CONSTANTS.CURRENCY
        if (this.presentmentCurrency)
          eventProperties.presentment_currency = this.presentmentCurrency
        if (this.language) eventProperties.language = this.language
        if (this.locale) eventProperties.locale = this.locale
        const ometriaProfileId = await this.ometriaProfileId
        if (ometriaProfileId)
          eventProperties.ometria_profile_id = ometriaProfileId

        analytics.track(name, eventProperties, this.consentProperties)

        // @gtm
        this.dataLayerPush(name, eventProperties)

        // @intelligems
        window.igEvents = window.igEvents || []
        window.igEvents.push({
          event: name,
          properties: eventProperties,
        })
      })
      .catch((error) => {
        console.warn('Track could not set common properties.')
        console.log(error.message)
      })
  }

  /**
   * page
   *
   * Makes Segment's page call.
   *
   * @param  string   The page title
   * @param  object   The page properties
   */
  async page(name, data) {
    const eventData = data || {}
    if (typeof analytics.page === 'function') {
      eventData.device_category = this.deviceCategory
      const ometriaProfileId = await this.ometriaProfileId
      if (ometriaProfileId) eventData.ometria_profile_id = ometriaProfileId
      analytics.page(name, eventData, this.consentProperties)
      this.dataLayerPush(this.CONSTANTS.EVENTS.PAGE_VIEWED, eventData)
    }
  }

  /**
   * Wraps segment calls into a try/catch to avoid these calls
   * creating side effects to the root customer experience.
   */
  protect(cb) {
    try {
      cb()
    } catch (error) {
      console.warn('Error with Segment call')
      console.log(error)
    }
  }

  /**
   * Pushes a Segment event to the Google Tag Manager datalayer.
   *
   * @param  string   The event name
   * @param  object   The event properties
   */
  dataLayerPush(name, data) {
    const dataForGTM = structuredClone(data) || {}
    dataForGTM.event = name
    dataForGTM.sent_from = 'website'

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(dataForGTM)
  }

  setOmetriaProfileId(providedId) {
    let id = false
    if (providedId) {
      id = providedId
    } else {
      const ometriaIdFromCookie = this.getOmetriaIdFromCookie()
      if (ometriaIdFromCookie) {
        id = ometriaIdFromCookie
      } else {
        const ometriaIdFromIdentify = this.getOmetriaIdFromIdentity()
        if (ometriaIdFromIdentify) {
          id = ometriaIdFromIdentify
        }
      }
    }
    return id
  }

  getOmetriaIdFromCookie() {
    let id
    const ometriaCookie = getCookie('ometria')

    if (window.cookieStore) {
      // iOS < 18.4 does not support cookieStore API
      const ometriaCookieChangeCallback = ({ changed, deleted }) => {
        for (const { name, value } of changed) {
          if (name === 'ometria') {
            if (value?.length) {
              const ometriaCookieValue = decodeURIComponent(value)
              const ometriaCookieParams = ometriaCookieValue.split('&')
              const omidParam = ometriaCookieParams?.find((param) =>
                param.startsWith('uiid='),
              )
              window.cookieStore.removeEventListener(
                'change',
                ometriaCookieChangeCallback,
              )
              id = this.formatOmetriaIdentity(omidParam)
              return id
            }
          }
        }
      }
      window.cookieStore.addEventListener('change', ometriaCookieChangeCallback)
    } else if (ometriaCookie?.length) {
      const ometriaCookieValue = decodeURIComponent(ometriaCookie)
      const ometriaCookieParams = ometriaCookieValue.split('&')
      const omidParam = ometriaCookieParams?.find((param) =>
        param.startsWith('uiid='),
      )
      if (omidParam) {
        id = this.formatOmetriaIdentity(omidParam)
      }
      return id
    } else {
      id = false
      return id
    }
  }

  getOmetriaIdFromIdentity() {
    const identity = window.ometria.getIdentity()
    if (!identity) return false

    const id = this.formatOmetriaIdentity(identity)
    return id
  }

  getOmetriaProfileId() {
    return new Promise((resolve) => {
      const ometriaIdFromCookie = this.getOmetriaIdFromCookie()
      if (ometriaIdFromCookie) {
        const id = this.setOmetriaProfileId(ometriaIdFromCookie)
        resolve(id)
      } else {
        const ometriaLoadPromise = new Promise((resolve) => {
          if (window.ometria) {
            resolve(true)
          } else {
            window.onOmetriaLoaded = () => {
              setTimeout(() => {
                resolve(true)
              }, 500)
            }
          }
        })
        const fallbackTimer = new Promise((resolve) => {
          window.setTimeout(() => {
            resolve(false)
          }, 6000)
        })
        const waitToLoadOmetria = Promise.race([
          ometriaLoadPromise,
          fallbackTimer,
        ])
        waitToLoadOmetria.then((result) => {
          if (result) {
            const id = this.setOmetriaProfileId()
            resolve(id)
          } else {
            resolve(false)
          }
        })
      }
    })
  }

  /**
   * getIdentity may return a string prefixed with 'omid:', or an email address.
   * This function returns a formatted, decoded id.
   */
  formatOmetriaIdentity(identity) {
    let id
    if (identity?.includes('omid:')) {
      id = identity.split('omid:')[1]
    } else if (identity?.includes('uiid=')) {
      id = identity.split('uiid=')[1]
    } else {
      id = identity
    }

    id = decodeURIComponent(id)
    if (id.includes('@')) this.email = id
    return id
  }

  /**
   * Waits for Shopify object to be available and then
   * assigns some common properties from that object
   */
  async accessCommonProperties() {
    if (window.Shopify) {
      this.setPropertiesFromShopify()
    } else {
      let retriesRemaining = 12
      const shopifyReady = await new Promise((resolve) => {
        while (!Object.prototype.hasOwnProperty.call(window, 'Shopify')) {
          setTimeout(() => {
            if (retriesRemaining === 0) {
              resolve(false)
            } else {
              retriesRemaining--
            }
          }, 250)
        }
        resolve(true)
      })
      if (shopifyReady) {
        this.setPropertiesFromShopify()
      }
    }
  }

  /**
   * Properties from the Shopify object that should be
   * set on Segment events.
   */
  setPropertiesFromShopify() {
    this.country = window.Shopify?.country?.toLowerCase()
    this.language =
      window.Shopify?.locale?.toLowerCase() ||
      window.Shopify?.Checkout?.locale?.toLowerCase() ||
      undefined
    this.locale =
      window.Shopify?.routes?.root === '/'
        ? 'en-us'
        : window.Shopify.routes.root.replaceAll('/', '').toLowerCase()
    this.presentmentCurrency = window.Shopify?.currency?.active
    if (window.ShopifyAnalytics?.meta?.page?.customerId) this.signedIn = true
    this.cartToken = getCookie('cart')

    // Required for Segment consent management
    // https://segment.com/docs/privacy/consent-management/consent-in-segment-connections/#consent-object
    const preferences = window.Shopify?.customerPrivacy?.currentVisitorConsent()
    if (preferences) {
      this.consentProperties = {
        consent: {
          categoryPreferences: {
            Advertising: preferences.marketing,
            Analytics: preferences.analytics,
            Functional: preferences.preferences,
            DataSharing: preferences.sale_of_data,
          },
        },
      }
    }
  }
}

const eventNames = {
  // Header
  ACCOUNT_ICON_CLICKED: 'Account Icon Clicked',
  ACCOUNT_MENU_ITEM_CLICKED: 'Account Menu Item Clicked',
  CART_ICON_CLICKED: 'Cart Icon Clicked',
  HEADER_LOGO_CLICKED: 'Header Logo Clicked',
  LOGIN: 'Login',
  LOGIN_LINK_CLICKED: 'Login Link Clicked',
  PRODUCTS_SEARCHED: 'Products Searched',
  SEARCH_ICON_CLICKED: 'Search Icon Clicked',
  SEARCH_SUGGESTED_TERM_CLICKED: 'Search Suggested Term Clicked',
  WISHLIST_ICON_CLICKED: 'Wishlist Icon Clicked',

  // Content
  ACCORDION_OPENED: 'Accordion Opened',
  BANNER_BLOCK_CLICKED: 'Banner Block Clicked',
  BANNER_BLOCK_SCROLLED: 'Banner Block Scrolled',
  BIS_SUBMISSION: 'BIS Submission',
  CATEGORIES_BLOCK_CLICKED: 'Categories Block Clicked',
  CROSS_SELL_BLOCK_CLICKED: 'Cross Sell Block Clicked',
  EMPC_SCROLLED: 'Editorial Multi-Product Carousel Scrolled',
  EMPC_PRODUCT_CLICKED: 'Editorial Multi-Product Carousel Product Clicked',
  EMPC_LINK_CLICKED: 'Editorial Multi-Product Carousel Link Clicked',
  EPC_SCROLLED: 'Editorial Product Carousel Scrolled',
  EPC_PRODUCT_CLICKED: 'Editorial Product Carousel Product Clicked',
  EPC_LINK_CLICKED: 'Editorial Product Carousel Link Clicked',
  FLEXIBLE_BLOCK_CLICKED: 'Flexible Block Clicked',
  HORIZONTAL_SLIDER_CLICKED: 'Horizontal Slider Clicked',
  HORIZONTAL_SLIDER_SCROLLED: 'Horizontal Slider Scrolled',
  HOT_SPOT_EXPANDED: 'Hot Spot Expanded',
  HOT_SPOT_LINK_CLICKED: 'Hot Spot Link Clicked',
  HYPE_BANNER_CLICKED: 'Hype Banner Clicked',
  IMAGE_GRID_CTA_CLICKED: 'Image Grid CTA Clicked',
  INTERACTIVE_ACCORDION_CLICKED: 'Interactive Accordion Clicked',
  MISSION_BANNER_CLICKED: 'Mission Banner Clicked',
  MODAL_CTA_CLICKED: 'Modal CTA Clicked',
  MODAL_SCROLLED: 'Modal Scrolled',
  NOTIFICATION_BAR_CLICKED: 'Notification Bar Clicked',
  NOTIFICATION_BAR_SCROLLED: 'Notification Bar Scrolled',
  TERMS_CLICKED: 'Offer Terms and Conditions Clicked',
  PRODUCT_GRID_IMAGE_CLICKED: 'Product Grid Clicked',
  SPLIT_CAROUSEL_CLICKED: '50/50 Carousel Clicked',
  TRANSPARENT_CAROUSEL_CLICKED: 'Transparent Carousel Clicked',
  UGC_POST_CLICKED: 'UGC Post Clicked',
  UGC_POST_PRODUCT_ADDED: 'UGC Product Added to Cart',
  UGC_POST_PRODUCT_CLICKED: 'UGC Product Clicked',

  // Cart
  CART_ADDON_CLICKED: 'Cart Addon Clicked',
  CART_ADDON_CTA_CLICKED: 'Cart Addon CTA Clicked',
  CART_CHECKOUT_BUTTON_CLICKED: 'Cart Checkout Button Clicked',
  CART_ITEM_QUANTITY_INCREASED: 'Cart Item Quantity Increased',
  CART_ITEM_QUANTITY_DECREASED: 'Cart Item Quantity Decreased',
  CART_ITEM_SAVED_FOR_LATER: 'Cart Item Saved for Later',
  SAVE_FOR_LATER_MOVED_TO_CART: 'Save for Later Moved to Cart',
  SAVE_FOR_LATER_QUANTITY_DECREASED: 'Save for Later Quantity Decreased',
  SAVE_FOR_LATER_QUANTITY_INCREASED: 'Save for Later Quantity Increased',
  CART_VIEWED: 'Cart Viewed',
  CART_EMPTY_CONTENT_CLICKED: 'Empty Mini Cart Content Clicked',
  CART_UNDO_CLICKED: 'Cart Undo Clicked',

  // Collections
  COLLECTION_FEATURED_FILTER_CLICKED: 'Collection Featured Filter Clicked',
  FEATURED_BLOCK_CLICKED: 'Featured Collections Block Clicked',
  FILTER_ADDED: 'Filter Added',
  FILTER_REMOVED: 'Filter Removed',
  FILTERS_CLEARED: 'Filters Cleared',
  FILTERS_VIEWED: 'Filters Viewed',
  IN_GRID_CONTENT_CLICKED: 'In Grid Content Clicked',
  QUICK_LINK_CLICKED: 'Quick Link Clicked',
  SEARCHANDISING_BANNER_CLICKED: 'Searchandizing Banner Clicked',
  SORT_ADDED: 'Sort Added',
  SORTS_VIEWED: 'Sorts Viewed',

  // Forms
  EMAIL_SUBMITTED: 'Email Submitted',
  SMS_SUBMITTED: 'SMS Submitted',

  // Menus
  BREADCRUMB_CLICKED: 'Breadcrumb Clicked',
  COUNTRY_SWITCHED: 'Country Switched',
  COUNTRY_SWITCHER_OPENED: 'Country Switcher Opened',
  FOOTER_MENU_SECTION_EXPANDED: 'Footer Menu Section Expanded',
  FOOTER_MENU_ITEM_CLICKED: 'Footer Menu Item Clicked',
  LANGUAGE_SELECTOR_CLICKED: 'Language Selector Clicked',
  MAIN_MENU_CLOSED: 'Main Menu Closed',
  MAIN_MENU_ITEM_CLICKED: 'Main Menu Item Clicked',
  MAIN_MENU_OPENED: 'Main Menu Opened',
  MAIN_MENU_SECTION_EXPANDED: 'Main Menu Section Expanded',

  // Misc
  CHAT_CLICKED: 'Chat Clicked',
  EXPERIMENT_VIEWED: 'Experiment Viewed',
  PAGE_VIEWED: 'Loaded a Page',
  START_SESSION: 'Start Session',

  // Products
  EXPRESS_CHECKOUT_INITIATED: 'Express Checkout Initiated',
  JOIN_WAITLIST_CLICKED: 'Join Waitlist Clicked',
  PRODUCT_ADDED: 'Product Added',
  PRODUCT_ADDONS_SCROLLED: 'Product Addons Scrolled',
  PRODUCT_ADDONS_CLICKED: 'Product Addon Clicked',
  PRODUCT_ADDONS_CTA_CLICKED: 'Product Addon CTA Clicked',
  PRODUCT_CAROUSEL_CLICKED: 'Product Carousel Clicked',
  PRODUCT_CAROUSEL_CTA_CLICKED: 'Product Carousel CTA Clicked',
  PRODUCT_CAROUSEL_SCROLLED: 'Product Carousel Scrolled',
  PRODUCT_CAROUSEL_TAB_CLICKED: 'Product Carousel Tab Clicked',
  PRODUCT_CLICKED: 'Product Clicked',
  PRODUCT_CTA_CLICKED: 'Product CTA Clicked',
  PRODUCT_IMAGE_SCROLLED: 'Product Image Scrolled',
  PRODUCT_IMAGE_VIEWED: 'Product Image Viewed',
  PRODUCT_IMAGE_THUMBNAIL_CLICKED: 'Product Image Thumbnail Clicked',
  PRODUCT_LIST_VIEWED: 'Product List Viewed',
  PRODUCT_VIEWED: 'Product Viewed',
  QUICK_SHOP_DETAILS_CLICKED: 'Quick Shop Full Details Clicked',
  QUICK_SHOP_VARIANT_CLICKED: 'Quick Shop Variant Clicked',
  QUICK_VIEW_OPENED: 'Quick View Opened',
  QUICK_VIEW_FULL_DETAILS_CLICKED: 'Quick View Full Details Clicked',
  REVIEW_STARS_CLICKED: 'Review Stars Clicked',
  SIZE_CHART_CLICKED: 'Size Chart Clicked',
  SIZE_SYSTEM_SELECTED: 'Size System Selected',
  SWATCH_CLICKED: 'Swatch Clicked',
  VARIANT_CLICKED: 'Variant Clicked',
  VIEW_ALL_COLORS_CLICKED: 'View All Colors Clicked',

  // Stores
  STORE_AVAILABILITY_STORE_CLICKED: 'Store Name Clicked',
  STORE_AVAILABILITY_CHECK_ADDITIONAL: 'Check Additional Stores Clicked',
  STORE_AVAILABILITY_VIEW_ALL: 'View All Stores Clicked',
  STORE_CLICKED: 'Store Clicked',
  STORE_ACCORDION_OPENED: 'Store Accordion Opened',
  STORE_CONTENT_BLOCK_CLICKED: 'Store Content Block Clicked',
  STORE_DIRECTIONS_CLICKED: 'Store Directions Clicked',
  STORES_FILTER_CLICKED: 'Stores Location Type Clicked',
  STORES_GEOLOCATED: 'Use Current Location Clicked',
  STORES_REGION_CLICKED: 'Stores Region Clicked',
  STORES_SEARCHED: 'Stores Searched',

  // Video
  VIDEO_CC_TOGGLED: 'Video CC Toggled',
  VIDEO_COMPLETED: 'Video Completed',
  VIDEO_MUTE_TOGGLED: 'Video Mute Toggled',
  VIDEO_PAUSED: 'Video Paused',
  VIDEO_PLAYED: 'Video Played',
  VIDEO_PLAYER_LINK_CLICKED: 'Video Player Clicked',
  VIDEO_PROGRESS: 'Video Progress Updated',

  // Wishlist
  WISHLIST_HEART_CLICKED: 'Wishlist Heart Clicked',
}

export const Segment = new SegmentClient()

// Make analytics tracking available outside of JS modules
const registerGlobal = window.DNARegisterGlobal
if (registerGlobal) registerGlobal(Segment, 'Segment', 'vendor')
